import React, { useState } from "react";
import { Box, Modal, Typography, Button, CircularProgress, Grid } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

import type { RootState } from "../../../store/state/rootReducer";
import type { AppDispatch } from "../../../store/state/rootStore";
import useMixPanel from "../../../lib/mixpanel";
import TSSecondaryButton from "../../../components/common/buttons/TSSecondaryButton";
import { notifyError, notifySuccess } from "../../../store/actions/notify";
import { downloadStory } from "../../../components/VideoCardList/utils/downloadStory";
import { clearSelectedStories } from "../../../store/state/stories/actions";

interface BulkDownloadModalProps {
  showModal: boolean;
  onCancel: () => void;
}

export const BulkDownloadModal = ({ showModal, onCancel }: BulkDownloadModalProps) => {
  /**
   * state
   */
  const [isDownloadingInterviews, setIsDownloadingInterviews] = useState(false);

  /**
   * redux
   */
  const dispatch = useDispatch<AppDispatch>();
  const selectedStories = useSelector((state: RootState) => state.stories.selectedStories);
  const selectedStoriesIds = selectedStories.map((story) => story._id);

  /**
   * helpers
   */
  const handleBulkDownload = async () => {
    try {
      useMixPanel("bulk_download_stories", { stories: selectedStoriesIds });
      setIsDownloadingInterviews(true);

      dispatch(clearSelectedStories());

      const failedDeletes: string[] = [];

      for (let i = 0; i < selectedStories.length; i++) {
        const story = selectedStories[i];
        try {
          downloadStory(story);

          if (i < selectedStories.length - 1) {
            await new Promise((resolve) => {
              setTimeout(resolve, 1500);
            });
          }
        } catch (error) {
          console.error(`Failed to start download story with ID: ${story._id}`, error);
          failedDeletes.push(story._id);
        }
      }

      if (failedDeletes.length > 0) {
        dispatch(notifyError(`Error starting download for ${failedDeletes.length} interview(s). Please try again.`));
      } else {
        dispatch(notifySuccess("Interviews are being downloaded"));
      }
      setIsDownloadingInterviews(false);
      onCancel();
    } catch (error) {
      console.error("Bulk download failed", error);
      dispatch(notifyError("Error downloading interviews"));
      setIsDownloadingInterviews(false);
    }
  };

  /**
   * render
   */
  return (
    <Modal
      open={showModal}
      onClose={onCancel}
      aria-labelledby="modal-bulk-download-title"
      aria-describedby="modal-bulk-download-description"
      sx={{ top: "30%", margin: "auto", maxWidth: "650px" }}
    >
      <Box sx={{ background: "white", maxWidth: "80%", margin: "5% auto", padding: "24px" }}>
        <Typography
          variant="h6"
          sx={{
            color: "rgba(0, 0, 0, 0.87)",
            fontSize: "20px",
            lineHeight: "32px",
            fontWeight: 500,
            marginBottom: "16px",
          }}
        >
          Download {selectedStoriesIds.length} {selectedStoriesIds.length > 1 ? "interviews" : "interview"}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "rgba(0, 0, 0, 0.87)",
            fontSize: "16px",
            lineHeight: "24px",
            fontWeight: 400,
            marginBottom: "24px",
          }}
        >
          This will download the selected interviews. Could take some time depending on the number of interviews.
        </Typography>

        <Box sx={{ width: "100%", gap: 20 }}>
          <Grid container sx={{ alignContent: "center" }}>
            <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
              <TSSecondaryButton text="Cancel" onClick={onCancel} styles={{ marginRight: "10px" }} />
              <Button
                onClick={handleBulkDownload}
                sx={{
                  textTransform: "none",
                  width: "180px",
                  "&:hover": {
                    outline: "none",
                  },
                  "&:focus": {
                    outline: "none",
                  },
                }}
                disabled={isDownloadingInterviews}
                variant="contained"
                color="primary"
              >
                {isDownloadingInterviews ? <CircularProgress size="20px" /> : "Download"}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Modal>
  );
};
